import React, { useState } from "react";
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Alert, Button, CardContent, TextField } from "@mui/material";
import FormFieldLayout from "../components/layout/FormFieldLayout";
import FormControlsLayout from "../components/layout/FormControlsLayout";
import PageLayout from "../components/layout/PageLayout";
import {useTranslation} from "react-i18next";

const SetPasswordPage = () => {
    const {t} = useTranslation();
    const [searchParams] = useSearchParams();
    const emailFromUrl = searchParams.get('email') || '';
    const tokenFromUrl = searchParams.get('token') || '';

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [message, setMessage] = useState(null);
    const [isError, setIsError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleSubmit = () => {
        setMessage(null);
        if (!emailFromUrl || !tokenFromUrl) {
            setMessage(t("auth.setPassword.messages.invalidLink"));
            setIsError(true);
            return;
        }
        const pwd = newPassword?.trim();
        const confirm = confirmPassword?.trim();
        if (!pwd) {
            setMessage(t("auth.setPassword.messages.enterPassword"));
            setIsError(true);
            return;
        }
        if (pwd !== confirm) {
            setMessage(t("auth.setPassword.messages.passwordMismatch"));
            setIsError(true);
            return;
        }
        setLoading(true);
        fetch('/api/SetPasswordRq', {
            method: 'POST',
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                email: emailFromUrl,
                token: tokenFromUrl,
                newPassword: pwd,
            }),
        })
            .then((response) => response.json())
            .then((body) => {
                if (body.status === 'OK') {
                    setSuccess(true);
                    setMessage(t("auth.setPassword.messages.success"));
                    setIsError(false);
                } else if (body.status === 'NOT_EXIST') {
                    setMessage(t("auth.setPassword.messages.notExist"));
                    setIsError(true);
                } else if (body.status === 'INVALID_TOKEN') {
                    setMessage(t("auth.setPassword.messages.invalidToken"));
                    setIsError(true);
                } else if (body.status === 'EXPIRED_LINK') {
                    setMessage(t("auth.setPassword.messages.expiredLink"));
                    setIsError(true);
                } else if (body.status === 'INVALID_PASSWORD') {
                    setMessage(t("auth.setPassword.messages.invalidRequest"));
                    setIsError(true);
                } else {
                    setMessage(t("auth.setPassword.messages.generic"));
                    setIsError(true);
                }
            })
            .catch((err) => {
                console.error('[SetPasswordRq] error', err);
                setMessage(t("auth.setPassword.messages.generic"));
                setIsError(true);
            })
            .finally(() => setLoading(false));
    };

    return (
        <PageLayout
            title={t("auth.setPassword.title")}
            showMenuButton={false}
            showNavigationMenu={false}
            cardStyle={{ marginLeft: 330, marginTop: 10, marginRight: 10, marginBottom: 10 }}
        >
            <CardContent>
                {message && (
                    <FormFieldLayout>
                        <Alert severity={isError ? "error" : "success"} onClose={() => setMessage(null)}>
                            {message}
                        </Alert>
                    </FormFieldLayout>
                )}
                {!success ? (
                    <>
                        {emailFromUrl && (
                            <FormFieldLayout>
                                <TextField
                                    label={t("auth.setPassword.email")}
                                    variant="standard"
                                    sx={{ width: 300 }}
                                    value={emailFromUrl}
                                    disabled
                                    fullWidth
                                />
                            </FormFieldLayout>
                        )}
                        <FormFieldLayout>
                            <TextField
                                required
                                label={t("auth.setPassword.newPassword")}
                                variant="standard"
                                sx={{ width: 300 }}
                                type="password"
                                value={newPassword}
                                onChange={(e) => { setNewPassword(e.target.value); setMessage(null); }}
                                disabled={loading}
                            />
                        </FormFieldLayout>
                        <FormFieldLayout>
                            <TextField
                                required
                                label={t("auth.setPassword.confirmNewPassword")}
                                variant="standard"
                                sx={{ width: 300 }}
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => { setConfirmPassword(e.target.value); setMessage(null); }}
                                disabled={loading}
                            />
                        </FormFieldLayout>
                        <FormControlsLayout>
                            <Button variant="contained" onClick={handleSubmit} disabled={loading}>
                                {loading ? t("auth.setPassword.updating") : t("auth.setPassword.submit")}
                            </Button>
                            <Button variant="outlined" component={RouterLink} to="/signin" disabled={loading}>
                                {t("auth.setPassword.signIn")}
                            </Button>
                            <Button variant="outlined" component={RouterLink} to="/recovery" disabled={loading}>
                                {t("auth.setPassword.passwordRecovery")}
                            </Button>
                        </FormControlsLayout>
                    </>
                ) : (
                    <FormControlsLayout>
                        <Button variant="contained" component={RouterLink} to="/signin">
                            {t("auth.setPassword.signIn")}
                        </Button>
                        <Button variant="outlined" component={RouterLink} to="/">
                            {t("auth.setPassword.home")}
                        </Button>
                    </FormControlsLayout>
                )}
            </CardContent>
        </PageLayout>
    );
};

export default SetPasswordPage;
